import produce from 'immer'
import { useLayoutEffect, useState } from 'react'
import { DropResult } from '@richardrout/react-smooth-dnd'

import {
    BoardEvent,
    DragDropData,
    DragDropListsEvents,
    DroppableItemType,
    DroppableListType,
    ItemListBasicType,
    OnItemMoveArgs,
    OptimisticLocalState,
    ServerEventStatus,
    State
} from '../dtos'

export interface MoveItemProps {
    onItemMoveServer?: DragDropListsEvents['onItemMove']
    optimisticOptions?: any
    setOptimisticState: React.Dispatch<
        React.SetStateAction<OptimisticLocalState>
    >
    setState: React.Dispatch<React.SetStateAction<State>>
    state: State
}

export const useMoveItem = (props: MoveItemProps) => {
    const { onItemMoveServer, setOptimisticState, setState, state } = props

    const [moveLocalProps, setMoveLocalProps] = useState<{
        id?: string
        sourceId?: string
        sourceIndex?: number
        targetIndex?: number
        targetId?: string
    }>({})

    // onItemMove
    // With react-smooth-dnd, onItemMove is procedural: it loops over the lists and returns the lists' props.
    // Unfortunately, it does not provide us with the following info in one go: itemId 123, is moved from colId 1 / index 0, to colId 4 / index 5.
    // Hence the use of useEffect and localState to get all the info needed before triggering onItemMove
    useLayoutEffect(() => {
        if (
            moveLocalProps &&
            moveLocalProps.sourceId &&
            moveLocalProps.targetId
        ) {
            const { sourceId, sourceIndex, targetId } = moveLocalProps
            // @ts-ignore
            let targetIndex: number = moveLocalProps.targetIndex

            const isDroppedInSameColumn = sourceId === targetId

            // We adjust the targetIndex in a "pessimistic" way.
            // We must move each card as low as possible, just above the next filtered item
            // when moving an item.
            if (state.filter.isFiltered) {
                const listIds = state.lists[targetId].itemIds
                const visibleItems = state.filter.visibleItems

                if (isDroppedInSameColumn) {
                    // Dropped in same column
                    // @ts-ignore
                    const isDroppedBefore = targetIndex < sourceIndex
                    // @ts-ignore
                    const isDroppedAfter = targetIndex > sourceIndex

                    if (isDroppedBefore) {
                        // do nothing
                    }

                    if (isDroppedAfter) {
                        // console.log('Dropped After')
                        // console.log('  visibleItems:', visibleItems)
                        // console.log('  listIds:', listIds)
                        // console.log('  sourceIndex:', sourceIndex)
                        // console.log('  targetIndex:', targetIndex)
                        const startIndex = targetIndex

                        // console.log('  Start loop')
                        for (let i = startIndex + 1; i < listIds.length; i++) {
                            const item = listIds[i]
                            // console.log('    item:', item)
                            const isVisibleItem = Boolean(
                                visibleItems?.find((vi) => vi === item)
                            )
                            // console.log('   isVisibleItem?:', isVisibleItem)
                            const visibleItemIndex = i
                            if (isVisibleItem) {
                                targetIndex = visibleItemIndex - 1
                                // console.log('    case 1:', targetIndex)
                                break
                            }
                            if (i === listIds.length - 1) {
                                targetIndex = listIds.length - 1
                                // console.log('    case 2', targetIndex)
                                break
                            }
                        }
                    }
                } else {
                    // Dropped in different column
                    let startIndex = targetIndex
                    for (let i = startIndex; i < listIds.length; i++) {
                        const item = listIds[i]
                        const foundNextVisibleItem =
                            visibleItems?.findIndex((vi) => vi === item) !== -1

                        if (foundNextVisibleItem) {
                            targetIndex = i
                            break
                        }
                        if (i === listIds.length - 1) {
                            targetIndex = listIds.length - 1
                            break
                        }
                    }
                }
            }

            // Do not move is dropped same place.
            // Warning! This does not always work. Check special case.
            if (
                !(
                    sourceIndex === moveLocalProps.targetIndex &&
                    isDroppedInSameColumn
                )
            ) {
                // console.log('ItemIds', state.lists[localState.targetId].itemIds)
                // console.log('targetIndex', targetIndex)
                // console.log(
                //     'Move after item:',
                //     state.lists[localState.targetId].itemIds[targetIndex]
                // )
                moveItem({ ...(moveLocalProps as any), targetIndex })
            }
            setMoveLocalProps({})
        }
    }, [moveLocalProps])

    const onDropList = (dropResult: DropResult) => {
        const { addedIndex, removedIndex } = dropResult
        const args = {
            // @ts-ignore
            id: state.listOrder[removedIndex],
            // @ts-ignore
            sourceId: state.listOrder[removedIndex],
            // @ts-ignore
            targetId: state.listOrder[addedIndex],
            sourceIndex: removedIndex,
            targetIndex: addedIndex,
            type: DroppableListType
        }
    }

    const onDropItem = (listId: string, dropResult: DropResult) => {
        const {
            addedIndex: targetIndex,
            removedIndex: sourceIndex,
            payload: id
        } = dropResult

        // Removes item from source list
        if (sourceIndex !== null && targetIndex === null) {
            setMoveLocalProps((localState) => {
                return {
                    ...localState,
                    id,
                    sourceId: listId,
                    sourceIndex
                }
            })
        }

        // Adds item to target list
        if (sourceIndex === null && targetIndex !== null) {
            setMoveLocalProps((localState) => {
                return {
                    ...localState,
                    id,
                    targetId: listId,
                    targetIndex
                }
            })
        }

        // Removes / Adds item in same list
        if (sourceIndex !== null && targetIndex !== null) {
            setMoveLocalProps({
                id,
                sourceId: listId,
                sourceIndex,
                targetId: listId,
                targetIndex
            })
        }
    }

    /**
     * Move Card or List
     * @param args
     */
    const moveItem = async (args: OnItemMoveArgs) => {
        const { targetId, targetIndex, id, sourceId, sourceIndex } = args

        if (targetId === undefined) {
            return
        }

        // Cancels if item dropped at same location
        if (targetId === sourceId && targetIndex === sourceIndex) {
            return
        }

        // Local (we always use optimistic with move.)
        // if (props.optimisticOptions) {
        onDragEnd(args as any)
        // }

        // Server, if provided
        if (onItemMoveServer) {
            setOptimisticState((state) => {
                const nextState = produce(state, (draftState) => {
                    const newEvent: OptimisticLocalState['event'][0] = {
                        // @ts-ignore
                        itemId: id,
                        type: BoardEvent.move,
                        stateBeforeEvent: {
                            items: props.state.itemsBasicProps,
                            listOrder: props.state.listOrder,
                            lists: props.state.lists
                        },
                        serverEventStatus: ServerEventStatus.Loading
                    }
                    draftState.event.push(newEvent)
                })
                return nextState
            })

            const targetNodeId =
                // @ts-ignore
                state.lists[moveLocalProps.targetId].itemIds[targetIndex]
            const onItemMoveArgs: OnItemMoveArgs = {
                id,
                targetNodeId,
                sourceId,
                sourceIndex,
                targetId,
                targetIndex,
                type: DroppableItemType
            }
            try {
                const response = await onItemMoveServer(onItemMoveArgs)
                setOptimisticState((state) => {
                    const nextState = produce(state, (draftState) => {
                        // @ts-ignore
                        draftState.event.find(
                            (e) => e.itemId === id
                        ).serverEventStatus = ServerEventStatus.Success
                    })
                    return nextState
                })
            } catch {
                setOptimisticState((state) => {
                    const nextState = produce(state, (draftState) => {
                        // @ts-ignore
                        draftState.event.find(
                            (e) => e.itemId === id
                        ).serverEventStatus = ServerEventStatus.Error
                    })
                    return nextState
                })
            }
        } else {
            console.log('Please provide a "onItemMove" props')
        }
    }

    const onDragEnd = (args: {
        id: string
        sourceId?: string
        sourceIndex: number
        targetId?: string
        targetIndex: number
        type?: string
    }) => {
        const { id, targetId, targetIndex, sourceId, sourceIndex, type } = args

        if (targetIndex === null) {
            return
        }

        // Moving list
        if (type === DroppableListType) {
            setState((state) => {
                const nextState = produce(state, (draftState) => {
                    draftState.listOrder.splice(sourceIndex, 1)
                    draftState.listOrder.splice(targetIndex, 0, id)
                })
                return nextState
            })
            return
        }

        // @ts-ignore
        const home = state.lists[sourceId]
        // @ts-ignore
        const foreign = state.lists[targetId]

        // Moving item within same list
        if (home === foreign) {
            setState((state) => {
                const nextState = produce(state, (draftState) => {
                    // @ts-ignore
                    draftState.lists[sourceId] &&
                        // @ts-ignore
                        draftState.lists[sourceId].itemIds.splice(
                            sourceIndex,
                            1
                        )
                    // @ts-ignore
                    draftState.lists[sourceId] &&
                        // @ts-ignore
                        draftState.lists[sourceId].itemIds.splice(
                            targetIndex,
                            0,
                            id
                        )
                })
                return nextState
            })
            return
        } else {
            // Moving item from one list to another
            setState((state) => {
                // console.log('Before Moving', state)
                const nextState = produce(state, (draftState) => {
                    // @ts-ignore
                    draftState.lists[sourceId] &&
                        // @ts-ignore
                        draftState.lists[sourceId].itemIds.splice(
                            sourceIndex,
                            1
                        )
                    // @ts-ignore
                    draftState.lists[targetId] &&
                        // @ts-ignore
                        draftState.lists[targetId].itemIds.splice(
                            targetIndex,
                            0,
                            id
                        )
                })
                // console.log('After Moving', state)
                return nextState
            })
            return
        }
    }

    return {
        onDropItem,
        onDropList
    }
}
